import { initializeApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  deleteDoc
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { 
  getAuth, 
  GoogleAuthProvider, 
  signInWithPopup, 
  signOut, 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword
} from 'firebase/auth';
// import { GoogleGenerativeAI } from '@google/generative-ai';
import { getVertexAI, getImagenModel, getGenerativeModel } from "firebase/vertexai";

const firebaseConfig = {
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY || "AIzaSyC2bUTpy7hQ0_Q93UWdmq3o1dRWSyB7qyc",
  authDomain: "boardbuilder-f6627.firebaseapp.com",
  projectId: "boardbuilder-f6627",
  storageBucket: "boardbuilder-f6627.firebasestorage.app",
  messagingSenderId: "1083460043898",
  appId: "1:1083460043898:web:ec81def0a225756eee27c3",
  measurementId: "G-R21SWBMW05"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const firestore = getFirestore(app);
const storage = getStorage(app);
const googleProvider = new GoogleAuthProvider();

export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    return result.user;
  } catch (error) {
    console.error("Error signing in with Google", error);
    throw error;
  }
};

export const signInWithEmail = async (email, password) => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  } catch (error) {
    console.error("Error signing in with email", error);
    throw error;
  }
};

export const registerWithEmail = async (email, password) => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    return result.user;
  } catch (error) {
    console.error("Error registering with email", error);
    throw error;
  }
};

export const signOutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error("Error signing out", error);
    throw error;
  }
};

export const saveCard = async (userId, cardData) => {
  try {
    const cardsRef = collection(firestore, 'cards');
    const docRef = await addDoc(cardsRef, {
      userId,
      ...cardData,
      createdAt: new Date()
    });
    return docRef.id;
  } catch (error) {
    console.error("Error saving card", error);
    throw error;
  }
};

export const updateCard = async (cardId, cardData) => {
  try {
    const cardRef = doc(firestore, 'cards', cardId); // Specify the document ID you want to update

    await updateDoc(cardRef, {
      ...cardData, // The data you want to update
      updatedAt: new Date() // Optionally, add an updated timestamp
    });

    return cardId; // Return the document ID after successful update
  } catch (error) {
    console.error("Error updating card", error);
    throw error;
  }
};

export const deleteCard = async (cardId) => {
  try {
    const cardRef = doc(firestore, 'cards', cardId);
    await deleteDoc(cardRef);
    return true;
  } catch (error) {
    console.error("Error deleting card", error);
    throw error;
  }
};

export const loadUserCards = async (userId) => {
  try {
    const cardsRef = collection(firestore, 'cards');
    const q = query(cardsRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error("Error loading cards", error);
    throw error;
  }
};

export const loadBoardGameCards = async (boardGameId) => {
  try {
    const cardsRef = collection(firestore, 'cards');
    const q = query(cardsRef, where('boardGameId', '==', boardGameId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error("Error loading board game cards", error);
    throw error;
  }
};

export const loadCard = async (cardId) => {
  try {
    const cardRef = doc(firestore, 'cards', cardId);
    const cardSnap = await getDoc(cardRef);
    
    if (!cardSnap.exists()) {
      throw new Error('Card not found');
    }
    
    return {
      id: cardSnap.id,
      ...cardSnap.data()
    };
  } catch (error) {
    console.error("Error loading card", error);
    throw error;
  }
};

export const loadCardVariants = async (parentCardId) => {
  try {
    const cardsRef = collection(firestore, 'cards');
    const q = query(cardsRef, where('parent_id', '==', parentCardId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error("Error loading card variants", error);
    throw error;
  }
};

// inviteEditor() lived here. It wrote a boardGameEditors row straight from the
// browser, which meant two ways to grant access with different rules — and the
// client-side one could not send the email or check the caller owned the game.
// Sharing goes through PUT /v1/games/{id}/collaborators now.

export const getEditors = async (boardGameId) => {
  try {
    const editorsRef = collection(firestore, 'boardGameEditors');
    const q = query(editorsRef, where('boardGameId', '==', boardGameId));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error("Error getting editors", error);
    throw error;
  }
};

export const updateEditorStatus = async (editorId, status) => {
  try {
    const editorRef = doc(firestore, 'boardGameEditors', editorId);
    await updateDoc(editorRef, {
      status,
      updatedAt: new Date()
    });
    
    return true;
  } catch (error) {
    console.error("Error updating editor status", error);
    throw error;
  }
};

export const followBoardGame = async (userId, boardGameId) => {
  try {
    const followsRef = collection(firestore, 'boardGameFollows');
    const q = query(
      followsRef,
      where('userId', '==', userId),
      where('boardGameId', '==', boardGameId)
    );
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      // User already follows this game, unfollow it
      const followDoc = querySnapshot.docs[0];
      await deleteDoc(doc(firestore, 'boardGameFollows', followDoc.id));
      return false; // Return false to indicate unfollowed
    }
    
    // Add new follow
    await addDoc(followsRef, {
      userId,
      boardGameId,
      followedAt: new Date()
    });
    return true; // Return true to indicate followed
  } catch (error) {
    console.error("Error following/unfollowing board game", error);
    throw error;
  }
};

export const isFollowingBoardGame = async (userId, boardGameId) => {
  try {
    const followsRef = collection(firestore, 'boardGameFollows');
    const q = query(
      followsRef,
      where('userId', '==', userId),
      where('boardGameId', '==', boardGameId)
    );
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error("Error checking follow status", error);
    throw error;
  }
};

export const getFollowedBoardGames = async (userId) => {
  try {
    const followsRef = collection(firestore, 'boardGameFollows');
    const q = query(followsRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    
    const followedGames = [];
    for (const followDoc of querySnapshot.docs) {
      const boardGameRef = doc(firestore, 'boardGames', followDoc.data().boardGameId);
      const boardGameSnap = await getDoc(boardGameRef);
      
      if (boardGameSnap.exists()) {
        followedGames.push({
          id: boardGameSnap.id,
          ...boardGameSnap.data(),
          followedAt: followDoc.data().followedAt
        });
      }
    }
    
    return followedGames;
  } catch (error) {
    console.error("Error getting followed board games", error);
    throw error;
  }
};

/**
 * Games somebody else owns that you have been given editor access to.
 *
 * Distinct from getUserInvites, which returns PENDING invitations — this is
 * the ones already accepted, i.e. games you can actually open and work on.
 */
export const getSharedBoardGames = async (userEmail) => {
  if (!userEmail) return [];
  try {
    const editorsRef = collection(firestore, 'boardGameEditors');
    const q = query(
      editorsRef,
      where('email', '==', userEmail),
      where('status', '==', 'accepted')
    );
    const snapshot = await getDocs(q);

    const games = [];
    for (const row of snapshot.docs) {
      const { boardGameId } = row.data();
      if (!boardGameId) continue;
      try {
        const gameSnap = await getDoc(doc(firestore, 'boardGames', boardGameId));
        // A game deleted after the invite leaves a dangling row; skip it
        // rather than rendering an empty tile.
        if (gameSnap.exists()) {
          games.push({ id: gameSnap.id, ...gameSnap.data() });
        }
      } catch (e) {
        console.warn('Shared game could not be read:', boardGameId, e);
      }
    }
    return games;
  } catch (error) {
    console.error('Error fetching shared board games:', error);
    return [];
  }
};

export const getUserInvites = async (userEmail) => {
  try {
    const editorsRef = collection(firestore, 'boardGameEditors');
    const q = query(
      editorsRef, 
      where('email', '==', userEmail),
      where('status', '==', 'pending')
    );
    const querySnapshot = await getDocs(q);
    
    // Get the board game details for each invite
    const invites = [];
    for (const docSnapshot of querySnapshot.docs) {
      const editorData = {
        id: docSnapshot.id,
        ...docSnapshot.data()
      };
      
      try {
        // Get the board game details
        const boardGameRef = doc(firestore, 'boardGames', editorData.boardGameId);
        const boardGameSnap = await getDoc(boardGameRef);
        
        if (boardGameSnap.exists()) {
          const boardGameData = boardGameSnap.data();
          
          // Get owner details
          let ownerName = 'Game Owner';
          let ownerEmail = '';
          
          if (boardGameData.userId) {
            const userRef = doc(firestore, 'users', boardGameData.userId);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              const userData = userSnap.data();
              ownerName = userData.displayName || ownerName;
              ownerEmail = userData.email || '';
            }
          }
          
          invites.push({
            ...editorData,
            boardGame: {
              id: boardGameSnap.id,
              ...boardGameData,
              ownerName,
              ownerEmail
            }
          });
        }
      } catch (err) {
        console.error('Error getting board game details:', err);
      }
    }
    
    return invites;
  } catch (error) {
    console.error("Error getting user invites", error);
    throw error;
  }
};

function convertBase64ImagesToSources(images) {
    return images.map(image => {
        // Check if bytesBase64Encoded exists and is not null/undefined
        if (image.bytesBase64Encoded) {
            // Attempt to detect image type or use a default
            const imageType = image.imageType || 'jpeg';
            
            return `data:image/${imageType};base64,${image.bytesBase64Encoded}`;
        }
        
        // Return null or a placeholder if no base64 data exists
        return null;
    }).filter(src => src !== null); // Remove any null sources
}

export const generateAIImage = async (prompt) => {
  try {
    // Initialize Firebase app if not already initialized
    // const app = initializeApp(firebaseConfig);

    // Initialize Vertex AI
    const vertexAI = getVertexAI(app)

    // Specify Imagen 2 model
    const imagenModel = getImagenModel(
      vertexAI,
      {
        model: "imagen-3.0-fast-generate-001",
        generationConfig: {
          numberOfImages: 1
        }
      }
    );

    // Prepare the image generation prompt
    const imagePrompt = `Create a vivid, high-quality board game header image based on this description: ${prompt}. 
    The image should be colorful, engaging, and representative of a board game theme.`;
    console.log(imagePrompt)

    // Generate content
    const response = await imagenModel.generateImages(imagePrompt)

       // If fewer images were generated than were requested,
    // then `filteredReason` will describe the reason they were filtered out
    if (response.filteredReason) {
      console.log(response.filteredReason);
    }

    if (response.images.length == 0) {
      throw new Error("No images in the response.")
    }

    const images = response.images;
    
    // Process the response
    let imageDataUrls = convertBase64ImagesToSources(images);

    console.log(imageDataUrls)
    // Return images or a placeholder
    return imageDataUrls.length > 0 
      ? imageDataUrls 
      : [
          'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAACklEQVR4nGMAAQAABQABDQottAAAAABJRU5ErkJggg=='
        ];

  } catch (error) {
    console.error("Error generating AI image:", error);
    
    // Return a default placeholder image
    return [
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAACklEQVR4nGMAAQAABQABDQottAAAAABJRU5ErkJggg=='
    ];
  }
};

/**
 * Generates a board game image using the Replicate API via Google Cloud Functions
 * @param {string} prompt - The description of the image to generate
 * @param {Object} options - Additional options for image generation
 * @returns {Promise<string[]>} - Array of image URLs
 */
export const generateGameImage = async (prompt, options = {}) => {
  try {
    // Routed through the Sparkforge API rather than calling the old
    // `generate_image` Cloud Function directly. Two reasons:
    //   1. That function was deployed --allow-unauthenticated, so anyone who
    //      found the URL could spend the project's image-generation budget.
    //      The API requires a credential and applies per-user quota.
    //   2. It returned an expiring provider URL, which the app then stored in
    //      Firestore — so card art silently 404'd later. The API copies the
    //      bytes into our own storage and returns a permanent URL.
    const { sparkforgeApi } = await import('@/sparkforgeApi');

    const result = await sparkforgeApi.generateImage({
      prompt: prompt || 'A colourful board game illustration',
      // `style` decides both the art direction and the shape. A header is a
      // full-colour banner; card art is deliberately uncoloured line art to
      // draw on, which is why a game header generated as a "card" came back
      // black and white.
      style: options.style
        || (options.board_image ? 'board' : (options.bypass_upscale ? 'raw' : 'card')),
      aspectRatio: options.aspect_ratio || '1:1',
      gameId: options.gameId || null
    });

    const urls = (result.images || []).map((i) => i.url).filter(Boolean);
    if (!urls.length) throw new Error('Image generation returned no images');

    if (result.placeholder) {
      console.warn(
        'Sparkforge image generation is not configured, so this is a labelled ' +
        'placeholder rather than generated art.'
      );
    }
    return urls;
  } catch (error) {
    console.error('Error generating game image:', error);
    throw error;
  }
};

// --- AI Playtester: Quick Read ---

const QUICK_READ_MAX_CHARS = 80000 // ~20k words, roughly 40 pages of rules
const QUICK_READ_MIN_CHARS = 100

export const QUICK_READ_LIMITS = {
  maxChars: QUICK_READ_MAX_CHARS,
  minChars: QUICK_READ_MIN_CHARS,
  maxFileSizeMB: 10,
  supportedTypes: ['application/pdf', 'text/plain', 'text/markdown']
}

export const runQuickRead = async (rulesText, gameContext = {}) => {
  if (!rulesText || rulesText.length < QUICK_READ_MIN_CHARS) {
    throw new Error(`Rules text is too short (${rulesText.length} chars). Provide at least ${QUICK_READ_MIN_CHARS} characters for a meaningful review.`)
  }
  if (rulesText.length > QUICK_READ_MAX_CHARS) {
    throw new Error(`Rules text is too long (${rulesText.length} chars). Maximum is ${QUICK_READ_MAX_CHARS} characters (~40 pages). Trim to your core rules.`)
  }

  const vertexAI = getVertexAI(app)
  const model = getGenerativeModel(vertexAI, {
    model: 'gemini-2.5-flash',
    generationConfig: {
      temperature: 0.7,
      maxOutputTokens: 4096
    }
  })

  const contextLine = gameContext.name
    ? `The game is called "${gameContext.name}"${gameContext.type ? ` and is a ${gameContext.type} game` : ''}.`
    : ''

  const prompt = `You are an experienced board game playtester reading a set of rules for the first time. Your job is to give the designer honest, specific, constructive feedback — the kind a knowledgeable friend would give before a real playtest.

${contextLine}

Analyze the following rules text and produce a structured review in valid JSON with these sections:

{
  "firstImpression": "2-3 sentences: what you understand the game to be after one read. What's clear, what's exciting, and what's confusing.",
  "ambiguities": [
    {
      "quote": "exact text from the rules",
      "issue": "why this is ambiguous",
      "suggestion": "how to clarify"
    }
  ],
  "missingRules": [
    {
      "situation": "the edge case or scenario",
      "why": "why a player would encounter this"
    }
  ],
  "setupGaps": [
    "specific thing that is unclear about initial game setup"
  ],
  "terminologyIssues": [
    {
      "term": "the problematic term",
      "issue": "inconsistent usage, undefined, or overloaded"
    }
  ],
  "suggestedRewrites": [
    {
      "severity": "critical|important|minor",
      "original": "exact original text",
      "rewrite": "suggested replacement",
      "reason": "why this rewrite helps"
    }
  ],
  "strengths": [
    "specific things the rules do well — clear structure, good examples, etc."
  ],
  "playtestQuestions": [
    "5 specific questions the designer should ask real playtesters based on the issues found"
  ]
}

Be specific. Quote the actual rules text when pointing out issues. Don't invent problems — if the rules are clear, say so. Limit ambiguities to the 10 most important. Limit suggested rewrites to the 3 most critical.

RULES TEXT:
${rulesText}`

  const result = await model.generateContent(prompt)
  const response = result.response
  const text = response.text()

  // Parse JSON from the response, handling markdown code blocks
  let jsonText = text
  const jsonMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/)
  if (jsonMatch) {
    jsonText = jsonMatch[1].trim()
  }

  try {
    return JSON.parse(jsonText)
  } catch (e) {
    console.error('Failed to parse Quick Read response as JSON:', e)
    return { rawResponse: text, parseError: true }
  }
}

export const savePlaytestReview = async (userId, boardGameId, mode, inputs, report) => {
  const reviewsRef = collection(firestore, 'playtestReviews')
  const reviewData = {
    userId,
    boardGameId: boardGameId || null,
    mode,
    status: 'complete',
    inputs: {
      description: inputs.description || null,
      rulesText: inputs.rulesText ? `${inputs.rulesText.substring(0, 200)}... (${inputs.rulesText.length} chars)` : null,
      fromApp: inputs.fromApp || false
    },
    report,
    playtestQuestions: report.playtestQuestions || [],
    revisionsUsed: 0,
    createdAt: new Date()
  }
  const docRef = await addDoc(reviewsRef, reviewData)
  return docRef.id
}

export const getPlaytestReviews = async (boardGameId) => {
  const reviewsRef = collection(firestore, 'playtestReviews')
  const q = query(reviewsRef, where('boardGameId', '==', boardGameId))
  const snapshot = await getDocs(q)
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
}

export { auth, firestore, storage };